/**
 * Katalóg splitrov — zdieľaná cache typov splitrov (symetrických/asymetrických).
 *
 * Naplní ju `splitterCatalogStore` po načítaní z API. Utility (opticalPathTracer,
 * gponNetworkGenerator) čítajú odtiaľto — s bezpečnou zálohou (6 štandardných typov),
 * takže výpočty fungujú aj pred načítaním / bez pripojenia.
 */

export interface SplitterCatalogItem {
  id?: number;
  name: string;                 // napr. "1:4", "Tap 90/10"
  is_symmetric: boolean;
  outputs_count: number;
  total_loss_db?: number | null;      // symetrické: jedna strata
  output_losses?: number[] | null;    // asymetrické (Fáza 2): strata per výstup
  color?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

// Predvolené (záloha) — zhodné s pôvodnou pevnou tabuľkou
const DEFAULTS: SplitterCatalogItem[] = [
  { name: '1:2', is_symmetric: true, outputs_count: 2, total_loss_db: 3.5, sort_order: 10 },
  { name: '1:4', is_symmetric: true, outputs_count: 4, total_loss_db: 7.0, sort_order: 20 },
  { name: '1:8', is_symmetric: true, outputs_count: 8, total_loss_db: 10.5, sort_order: 30 },
  { name: '1:16', is_symmetric: true, outputs_count: 16, total_loss_db: 13.5, sort_order: 40 },
  { name: '1:32', is_symmetric: true, outputs_count: 32, total_loss_db: 17.0, sort_order: 50 },
  { name: '1:64', is_symmetric: true, outputs_count: 64, total_loss_db: 20.5, sort_order: 60 },
];

let _catalog: SplitterCatalogItem[] = [...DEFAULTS];

/** Nastaví katalóg (volá store po načítaní z API). */
export function setSplitterCatalog(items: SplitterCatalogItem[]): void {
  if (Array.isArray(items) && items.length > 0) {
    _catalog = items;
  }
}

/** Vráti aktuálny katalóg (aktívne typy, zoradené). */
export function getSplitterCatalog(): SplitterCatalogItem[] {
  return _catalog;
}

/** Nájde typ podľa názvu. */
export function findSplitterType(name?: string | null): SplitterCatalogItem | undefined {
  if (!name) return undefined;
  return _catalog.find((s) => s.name === name) || DEFAULTS.find((s) => s.name === name);
}

/** Počet výstupov pre daný typ (fallback: parsovanie "1:N", inak 1). */
export function getSplitterOutputs(name?: string | null): number {
  const t = findSplitterType(name);
  if (t) return t.outputs_count;
  const m = (name || '').match(/(\d+)\s*$/);
  return m ? parseInt(m[1], 10) : 1;
}

/** Vložná strata (dB) pre daný typ (fallback: 0).
 *  Pre asymetrické (bez známeho výstupu) vráti priemer strát výstupov. */
export function getSplitterLoss(name?: string | null): number {
  const t = findSplitterType(name);
  if (t && !t.is_symmetric && Array.isArray(t.output_losses) && t.output_losses.length) {
    const vals = t.output_losses.filter((v) => typeof v === 'number') as number[];
    if (vals.length) return vals.reduce((a, b) => a + b, 0) / vals.length;
  }
  if (t && typeof t.total_loss_db === 'number') return t.total_loss_db;
  return 0;
}

/** Prepočet: podiel výkonu (%) → vložná strata (dB). excess = prídavná strata. */
export function percentToLossDb(percent: number, excess = 0): number {
  if (!percent || percent <= 0) return 99; // prakticky nulový výkon
  return -10 * Math.log10(percent / 100) + (excess || 0);
}

/** Prepočet: vložná strata (dB) → podiel výkonu (%). */
export function lossDbToPercent(lossDb: number, excess = 0): number {
  const net = lossDb - (excess || 0);
  return 100 * Math.pow(10, -net / 10);
}

/** Strata konkrétneho výstupu (dB) — pre asymetrické; inak celková strata typu. */
export function getSplitterOutputLoss(name?: string | null, outputIndex = 0): number {
  const t = findSplitterType(name);
  if (t && !t.is_symmetric && Array.isArray(t.output_losses) && t.output_losses[outputIndex] != null) {
    return t.output_losses[outputIndex] as number;
  }
  return getSplitterLoss(name);
}
